import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Heart, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. We'll reply soon!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Contact</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          Let's talk.<br />
          <span className="brush-underline brush-green">We're listening.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Whether you're a family seeking support, a partner wanting to help, or a future friend — write to us.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-5">
          <InfoCard color="#2196F3" icon={MapPin} title="Visit (soon)" body="A permanent home in Marion, Arkansas — opening on your support." />
          <InfoCard color="#E53935" icon={Heart} title="Director" body="Chatoria Franklin · for partnership and program inquiries." />
          <InfoCard color="#FFB300" icon={Mail} title="Write to us" body="Use the form on this page — every message reaches the director directly." />
          <a href="https://www.facebook.com/share/1DGrahrNKh/" target="_blank" rel="noreferrer"
             data-testid="contact-facebook-link"
             className="block rounded-3xl border border-black/5 bg-white p-6 hover:-translate-y-0.5 hover:shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading text-base font-semibold text-[#1F2937]">Follow on Facebook</div>
                <div className="text-sm text-[#6B7280] mt-1">See photos, events, and daily encouragement.</div>
              </div>
              <ExternalLink className="w-5 h-5 text-[#6B7280]" strokeWidth={1.6} />
            </div>
          </a>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl bg-white border border-black/5 p-8 md:p-10">
            {done ? (
              <div data-testid="contact-success" className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] mt-0.5" />
                <div>
                  <div className="font-heading text-xl font-semibold text-[#1F2937]">Thank you for reaching out</div>
                  <div className="text-[#4B5563] mt-1">We received your message. Chatoria or a team member will reply soon.</div>
                  <button onClick={() => setDone(false)} data-testid="contact-new-btn"
                          className="mt-4 text-sm text-[#2196F3] font-semibold hover:underline">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="contact-form">
                <Field label="Your name *">
                  <input required data-testid="contact-input-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Your full name" />
                </Field>
                <Field label="Email *">
                  <input required type="email" data-testid="contact-input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@example.com" />
                </Field>
                <Field label="Phone">
                  <input data-testid="contact-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="Optional" />
                </Field>
                <Field label="Subject">
                  <input data-testid="contact-input-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inputCls} placeholder="What's this about?" />
                </Field>
                <Field label="Message *" className="md:col-span-2">
                  <textarea required rows={5} data-testid="contact-input-message" value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} placeholder="Tell us how we can help…" />
                </Field>
                <div className="md:col-span-2 flex items-center justify-end">
                  <button type="submit" disabled={submitting} data-testid="contact-submit-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-7 py-3.5 text-sm font-semibold disabled:opacity-60">
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-2xl bg-[#F4F1EA] text-[#1F2937] placeholder:text-[#9CA3AF] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]";

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1.5 ${className}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{label}</span>
    {children}
  </label>
);

const InfoCard = ({ icon: Icon, title, body, color }) => (
  <div className="rounded-3xl bg-white border border-black/5 p-6 flex items-start gap-4">
    <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}15`, border: `1px solid ${color}33` }}>
      <Icon className="w-5 h-5" strokeWidth={1.6} style={{ color }} />
    </div>
    <div>
      <div className="font-heading font-semibold text-[#1F2937]">{title}</div>
      <div className="text-sm text-[#6B7280] mt-1">{body}</div>
    </div>
  </div>
);
